import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import appManager from '@app/appManager';
import { serviceState } from './state';
import { getUser } from './requireUser';
import { ServiceUserUtils } from './serviceUserUtils';

export type ServiceUserStatusEvent = {
  status: UserSetAvailabilityStatus;
  canChange: boolean;
};

export function notifyUserStatusChanged(
  status: UserSetAvailabilityStatus,
  canChange = true,
) {
  appManager.events.emit<ServiceUserStatusEvent>('user.status.updated', {
    status,
    canChange,
  });
}

function notifyUserStatusForceAway() {
  appManager.events.emit('user.status.force_away');
}

export function setUserStatus({
  userId,
  status,
  canChange,
}: {
  userId: string;
  status?: UserSetAvailabilityStatus;
  canChange?: boolean;
}) {
  const user = getUser(userId);
  if (!user) {
    return;
  }

  const availabilityStatus = ServiceUserUtils.getAvailabilityStatus(user);
  const prevStatus = availabilityStatus;
  const newStatus = status ?? availabilityStatus;

  if (prevStatus !== newStatus) {
    ServiceUserUtils.setAvailabilityStatus(user, newStatus);
    ServiceUserUtils.setOriginalStatus(user, newStatus);
  }

  if (!ServiceUserUtils.isUserPhase2()) {
    const prevCanChange = ServiceUserUtils.getCanChange(user);
    const newCanChange = canChange ?? prevCanChange;

    if (prevCanChange !== newCanChange) {
      ServiceUserUtils.setCanChange(user, newCanChange);
    }
  }
}

/**
 * Kazoo sets the user to unavailable on force away, so all we need to do is set the status locally and notify the app.
 * @param userId
 */
export function setUserUnavailable(userId: string) {
  const user = getUser(userId);
  if (!user) {
    return;
  }

  if (user.id === serviceState.id) {
    ServiceUserUtils.setCanChange(user, true);
    notifyUserStatusForceAway();
  }
  ServiceUserUtils.setAvailabilityStatus(
    user,
    UserSetAvailabilityStatus.Unavailable,
  );
}
